/** YouTube video catalogue — homepage panel, full page and in-page player. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, ArrowRight, ExternalLink } from "lucide-react";
import { Container, Section, Eyebrow, PageHeader, AccentItalic, Breadcrumb, Button, EmptyState, CardSkeleton, Pagination } from "../../components/ui";
import { api } from "../../lib/api";

function VideoCard({ v, onPlay }) {
  const [broken, setBroken] = useState(false);
  return (
    <button type="button" onClick={() => onPlay(v)} className="group text-left w-full" data-testid={`video-card-${v.slug || v.id}`}>
      <div className="relative aspect-video rounded-[18px] overflow-hidden border border-line-2 bg-surface">
        {!broken && v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt={v.title} loading="lazy" onError={() => setBroken(true)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-text-faint text-[12px] font-mono uppercase tracking-[0.16em]">Video</div>
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="w-14 h-14 rounded-full bg-black/55 backdrop-blur-md border border-white/30 grid place-items-center text-white transition-transform duration-300 group-hover:scale-110">
            <Play size={22} className="ml-0.5" fill="currentColor" />
          </span>
        </span>
      </div>
      <div className="mt-3 px-1">
        {v.category && <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-mauve mb-1">{v.category}</div>}
        <h3 className="font-display text-[18px] leading-snug line-clamp-2">{v.title}</h3>
        {v.description && <p className="text-text-dim text-[13px] mt-1 line-clamp-2">{v.description}</p>}
      </div>
    </button>
  );
}

function VideoPlayer({ v, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);
  if (!v) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="video-player-modal">
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 mb-3 text-white">
          <h3 className="font-display text-[20px] truncate">{v.title}</h3>
          <div className="flex items-center gap-2">
            <a href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[12.5px] text-white/80 hover:text-white" data-testid="video-open-youtube">
              Watch on YouTube <ExternalLink size={13} />
            </a>
            <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid="video-player-close"><X size={18} /></button>
          </div>
        </div>
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
          <iframe src={`${v.embed_url}?autoplay=1&rel=0`} title={v.title} className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      </div>
    </div>
  );
}

export function HomeVideos() {
  const [items, setItems] = useState(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => { api.get("/videos?limit=6").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  if (!items || items.length === 0) return null;
  return (
    <Section padded="sm" data-testid="home-videos">
      <Container>
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div>
            <Eyebrow>Watch</Eyebrow>
            <h2 className="font-display text-[clamp(24px,2.8vw,34px)] mt-3 tracking-[-0.01em]">Teachings on <AccentItalic>video</AccentItalic></h2>
          </div>
          <Button asChild variant="ghost" size="sm"><Link to="/videos" data-testid="home-videos-view-all">View all videos <ArrowRight size={14} /></Link></Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((v) => <VideoCard key={v.id} v={v} onPlay={setPlaying} />)}
        </div>
      </Container>
      <VideoPlayer v={playing} onClose={() => setPlaying(null)} />
    </Section>
  );
}

export function VideosPage() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [playing, setPlaying] = useState(null);
  useEffect(() => { setData(null); api.get(`/videos?limit=24&page=${page}`).then((r) => setData(r.data)).catch(() => setData({ items: [], meta: { page_count: 1 } })); }, [page]);
  return (
    <Section padded="sm" data-testid="videos-page">
      <Container>
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Videos" }]} />
        <PageHeader eyebrow="NVL on YouTube" title={<>Video <AccentItalic>library</AccentItalic></>} lede="Selected talks, practice guidance and teachings from Nikhil Vatika Lab." className="mt-6" />
        {!data && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>}
        {data && data.items.length === 0 && <EmptyState title="Videos coming soon" description="New teachings will appear here shortly." />}
        {data && data.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="videos-grid">
            {data.items.map((v) => <VideoCard key={v.id} v={v} onPlay={setPlaying} />)}
          </div>
        )}
        {data?.meta?.page_count > 1 && <div className="mt-12 flex justify-center"><Pagination page={data.meta.page} pageCount={data.meta.page_count} onPageChange={setPage} /></div>}
      </Container>
      <VideoPlayer v={playing} onClose={() => setPlaying(null)} />
    </Section>
  );
}
